import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { formatNumber } from "src/i18n/formatNumber";
import { PermissionService } from "src/services/permissionService/permissionService";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import {
    useCancelBuildMutation,
    useDeleteBuildMutation,
    useFetchProductByIdQuery,
    useUpdateBuildChannelMutation,
} from "@dashboard/products/api";
import { Build, Product } from "@dashboard/products/types";
import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { createBuildVariantTag, createRolloutTag, createTriggerTag, getTagReactKey, sortTags } from "@dashboard/shared/tags";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { DropdownItem } from "@devices/components/shared";
import { DialogContentText, Typography, useTheme } from "@mui/material";
import CompareService from "@services/compareService/compareService";
import TimeService from "@services/timeService/timeService";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { ActivityIcon } from "@shared/Loading";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage, useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";
import { Tooltip } from "@shared/Tooltip";

import { DownloadActionsMenu } from "./DownloadActionsMenu/DownloadActionsMenu";
import { BuildSettingsDialog } from "./SettingsDialog/BuildSettingsDialog";
import { Artifacts } from "./Artifacts";
import { isUploadingArtifacts } from "./artifactUploads";
import { BuildActionsMenu } from "./BuildActionsMenu";
import { useStyles } from "./Cells.style";
import { getFirmwareArtifacts, getFirmwareDownloads } from "./firmwareDownloads";
import { isBuildProcessing } from "./isBuildProcessing";

type ChangesTooltipProps = {
    kind: string;
    changes: string[];
};
const ChangesTooltip = (props: ChangesTooltipProps) => {
    const { t } = useTranslation(["pages"]);
    const { classes } = useStyles();
    let title = "-";

    switch (props.kind) {
        case "ADD":
            title = t("productPage.buildsTable.buildDiffTooltip.add");
            break;
        case "REMOVE":
            title = t("productPage.buildsTable.buildDiffTooltip.remove");
            break;
        case "REPLACE":
        case "UPDATE":
            title = t("productPage.buildsTable.buildDiffTooltip.update");
            break;
    }

    return (
        <React.Fragment>
            <Typography variant="subtitle2">{title}:</Typography>
            <ul className={classes.changesList}>
                {props.changes.map((value) => (
                    <li key={value}>
                        <Typography variant="caption">{value}</Typography>
                    </li>
                ))}
            </ul>
        </React.Fragment>
    );
};

type StatusCellProps = {
    buildStatus: API.Build["buildStatus"] | null;
    build: Build;
    productId: number;
};

export const StatusCell = (props: StatusCellProps) => {
    const { t } = useTranslation(["pages"]);
    const theme = useTheme();
    const { classes, cx } = useStyles();

    const productResult = useFetchProductByIdQuery(props.productId);
    const { data: product } = productResult;
    useQueryErrorToast(productResult);

    const compare = new CompareService(product?.customBuildConfig, props.build?.configSnapshot);
    const differencesReport = compare.getDifferencesReport();

    const getStatusIcon = () => {
        switch (props.buildStatus) {
            case "running":
                return (
                    <ActivityIcon
                        tooltip={t("pages:productPage.buildsTable.status.running")}
                        tooltipPlacement="right"
                        color={theme.palette.emteria.primary}
                    />
                );
            case "pending":
                return (
                    <Tooltip title={t("pages:productPage.buildsTable.status.pending")} placement="right">
                        <i className={cx("fa-solid fa-hourglass-half", classes.statusIconMuted)} />
                    </Tooltip>
                );
            case "canceled":
                return (
                    <Tooltip title={t("pages:productPage.buildsTable.status.canceled")} placement="right">
                        <i className={cx("fa-solid fa-ban", classes.statusIconMuted)} />
                    </Tooltip>
                );
            case "skipped":
                return (
                    <Tooltip title={t("pages:productPage.buildsTable.status.skipped")} placement="right">
                        <i className={cx("fa-regular fa-circle-right", classes.statusIconMuted)} />
                    </Tooltip>
                );
            case "succeeded":
                return (
                    <Tooltip title={t("pages:productPage.buildsTable.status.succeeded")} placement="right">
                        <i className={cx("fa-regular fa-circle-check", classes.statusIconSuccess)} />
                    </Tooltip>
                );
            case "failed":
                return (
                    <Tooltip title={t("pages:productPage.buildsTable.status.failed")} placement="right">
                        <i className={cx("fa-regular fa-circle-xmark", classes.statusIconError)} />
                    </Tooltip>
                );
            default:
                return (
                    <Tooltip title={t("pages:productPage.buildsTable.status.unknown")} placement="right">
                        <i className={cx("fa-regular fa-circle-question", classes.statusIconError)} />
                    </Tooltip>
                );
        }
    };

    return (
        <MinimalTableCell width={40}>
            {getStatusIcon()}
            {compare.hasDifferences ? (
                <Tooltip
                    placement="right"
                    title={Object.entries(differencesReport).map(([kind, changes]) => (
                        <ChangesTooltip key={kind} {...{ kind, changes }} />
                    ))}
                >
                    <i className={cx(classes.oldConfigIcon, "fa-solid fa-circle-info")} />
                </Tooltip>
            ) : null}
        </MinimalTableCell>
    );
};

type TimestampCellProps = {
    created: string | null;
    finished: string | null;
};
export const TimestampCell = (props: TimestampCellProps) => {
    const { t } = useTranslation(["pages"]);

    if (!props.created) {
        return (
            <MinimalTableCell width={100}>
                <Typography>-</Typography>
            </MinimalTableCell>
        );
    }

    const displayDate = TimeService.getLocalDate(props.created, "ISO", "date");
    const createdDateTime = TimeService.getLocalDate(props.created, "ISO", "dateTime");
    const finishedDateTime = props.finished ? TimeService.getLocalDate(props.finished, "ISO", "dateTime") : null;

    const createdLabel = t("pages:productPage.buildsTable.tooltip.created", { value: createdDateTime });
    const finishedLabel = finishedDateTime
        ? t("pages:productPage.buildsTable.tooltip.finished", { value: finishedDateTime })
        : null;

    const tooltipContent = (
        <React.Fragment>
            <Typography variant="body2">{createdLabel}</Typography>
            {finishedLabel ? <Typography variant="body2">{finishedLabel}</Typography> : null}
        </React.Fragment>
    );

    return (
        <MinimalTableCell width={100}>
            <Tooltip title={tooltipContent}>
                <Typography>{displayDate}</Typography>
            </Tooltip>
        </MinimalTableCell>
    );
};

type ArtifactsCellProps = {
    build: Build;
};
export const ArtifactsCell = (props: ArtifactsCellProps) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);

    const uploadedArtifacts = props.build.artifacts?.filter((item) => item.isUploaded) ?? [];
    const isUploading = isUploadingArtifacts(props.build);

    if (!uploadedArtifacts.length && !isUploading) {
        return (
            <MinimalTableCell>
                <Typography>-</Typography>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell>
            <div className={classes.artifactsCell}>
                {uploadedArtifacts.length ? <Artifacts artifacts={uploadedArtifacts} /> : null}
                {isUploading ? (
                    <ActivityIcon
                        tooltip={t("pages:productPage.buildsTable.artifactsUploading")}
                        color={theme.palette.emteria.primary}
                    />
                ) : null}
            </div>
        </MinimalTableCell>
    );
};

type DownloadsCellProps = {
    build: Build;
};
export const DownloadsCell = (props: DownloadsCellProps) => {
    const { i18n } = useTranslation();
    const hasFirmware = getFirmwareArtifacts(props.build).length > 0;

    return (
        <MinimalTableCell width={100}>
            <Typography>{hasFirmware ? formatNumber(getFirmwareDownloads(props.build), i18n.language) : "-"}</Typography>
        </MinimalTableCell>
    );
};

type TagsCellProps = {
    build: Build;
};
export const TagsCell = (props: TagsCellProps) => {
    const { classes } = useStyles();
    const removableTags = sortTags(props.build.metadata?.user?.tags ?? []);
    const generatedTags = sortTags(
        [
            createRolloutTag(props.build.rolloutPercentage),
            createBuildVariantTag(props.build),
            createTriggerTag(props.build),
        ].filter((tag) => tag !== null),
    );
    const hasTags = removableTags.length > 0 || generatedTags.length > 0;

    return (
        <MinimalTableCell className={classes.tagsBox}>
            {removableTags.map((item) => (
                <Tag key={getTagReactKey(item)} item={item} />
            ))}
            {generatedTags.map((item) => (
                <Tag key={getTagReactKey(item)} item={item} theme="light" />
            ))}
            {!hasTags ? <Typography>-</Typography> : null}
        </MinimalTableCell>
    );
};

const channelOptions = [
    { label: i18next.t("common:label.live"), value: "live" },
    { label: i18next.t("common:label.beta"), value: "beta" },
    { label: i18next.t("common:label.nightly"), value: "nightly" },
    { label: i18next.t("common:label.none"), value: "none" },
];

type ChannelCellProps = {
    build: Build;
};
export const ChannelCell = (props: ChannelCellProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const unreleasableBuildStatus = props.build.buildStatus !== "succeeded";
    const buildWithoutArtifacts = !props.build.artifacts?.some((artifact) => artifact.isUploaded);

    const [updateBuildChannel] = useUpdateBuildChannelMutation();

    const onChannelChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }
        const selectedChannel = selectedOption.value as Build["channel"];

        const result = await updateBuildChannel({
            productId: props.build.productId,
            buildId: props.build.buildId,
            channel: selectedChannel,
        });
        if ("error" in result) {
            showErrorToast(getErrorMessage(result.error));
        } else {
            showSuccessToast(t("pages:productPage.buildsTable.toast.channelChanged"));
        }
    };
    const getChannelInputTooltip = () => {
        if (cannotManageProducts) {
            return t("productPage.buildsTable.channelTooltip.forbiddenByRole");
        } else if (unreleasableBuildStatus) {
            return t("productPage.buildsTable.channelTooltip.unreleasableStatus");
        } else if (buildWithoutArtifacts) {
            return t("productPage.buildsTable.channelTooltip.withoutArtifacts");
        } else {
            return "";
        }
    };

    return (
        <MinimalTableCell>
            <Tooltip title={getChannelInputTooltip()}>
                <SelectField
                    className={classes.channelSelect}
                    value={channelOptions.find((option) => option.value === props.build.channel)}
                    options={channelOptions}
                    onChange={onChannelChange}
                    disabled={unreleasableBuildStatus || buildWithoutArtifacts || cannotManageProducts}
                    disableSorting
                />
            </Tooltip>
        </MinimalTableCell>
    );
};

type BuildActionsCellProps = {
    build: Build;
    product: Product;
};
export const BuildActionsCell = (props: BuildActionsCellProps) => {
    const [openSettingsDialog, setOpenSettingsDialog] = React.useState(false);
    const [isCanceling, setIsCanceling] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isDeleteBuildDialogOpen, setIsDeleteBuildDialogOpen] = React.useState(false);
    const { classes } = useStyles();

    const [cancelBuild] = useCancelBuildMutation();
    const [deleteBuild] = useDeleteBuildMutation();

    const { t } = useTranslation(["pages", "modals", "common"]);
    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    // Deleting stays allowed after expiry so the customer can clean up their own builds.
    const cannotDeleteProducts = productAbility(workspace).cannot("delete", "Product");
    // An expired subscription also revokes "manage Product", so it has to be reported ahead of
    // the role message - otherwise the tooltip blames the user's role for a billing state.
    const isExpired = useCurrentSubscriptionExpired();
    const expiredTooltip = t("pages:productPage.tooltip.subscriptionExpired");
    const buildProcessing = isBuildProcessing(props.build);

    const handleCancel = async () => {
        setIsCanceling(true);
        const result = await cancelBuild(props.build.buildId);
        if ("error" in result) {
            showErrorToast(getErrorMessage(result.error));
        } else {
            showSuccessToast(t("common:toast.buildCanceled"));
        }
        setIsCanceling(false);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        const result = await deleteBuild({ productId: props.build.productId, buildId: props.build.buildId });
        if ("error" in result) {
            showErrorToast(getErrorMessage(result.error));
        } else {
            showSuccessToast(t("pages:productPage.buildsTable.toast.buildDeleted"));
        }
        setIsDeleting(false);
        setIsDeleteBuildDialogOpen(false);
    };

    const settingsDisabledReason = isExpired ? expiredTooltip : t("pages:productPage.buildsTable.channelTooltip.forbiddenByRole");
    const cancelDisabledReason = isExpired
        ? expiredTooltip
        : t("pages:productPage.buildsTable.actionTooltip.noPermissionToCancelBuild");

    return (
        <MinimalTableCell width={120} style={{ overflow: "visible", position: "relative" }}>
            <div className={classes.actionBox}>
                <DownloadActionsMenu build={props.build} />
                <BuildActionsMenu
                    buildId={props.build.buildId}
                    hasLogs={props.build.hasLogs}
                    product={props.product}
                    workspaceGuid={workspace?.guid}
                >
                    <DropdownItem
                        label={t("pages:productPage.buildsTable.actionTooltip.editBuildSettings")}
                        iconName="fa-solid fa-fw fa-gear"
                        onClick={() => setOpenSettingsDialog(true)}
                        disabled={cannotManageProducts}
                        tooltipProps={{ title: settingsDisabledReason, hide: !cannotManageProducts }}
                    />
                    {buildProcessing ? (
                        <DropdownItem
                            label={t("pages:productPage.buildsTable.actionTooltip.cancelBuild")}
                            iconName="fa-solid fa-fw fa-ban"
                            danger
                            onClick={handleCancel}
                            disabled={isCanceling || cannotManageProducts}
                            tooltipProps={{ title: cancelDisabledReason, hide: !cannotManageProducts }}
                        />
                    ) : (
                        <DropdownItem
                            label={t("modals:deleteBuild.title")}
                            iconName="fa-solid fa-fw fa-trash"
                            danger
                            onClick={() => setIsDeleteBuildDialogOpen(true)}
                            disabled={isDeleting || cannotDeleteProducts}
                            tooltipProps={{
                                title: t("pages:productPage.buildsTable.actionTooltip.noPermissionToDeleteBuild"),
                                hide: !cannotDeleteProducts,
                            }}
                        />
                    )}
                </BuildActionsMenu>
            </div>
            {openSettingsDialog ? (
                <BuildSettingsDialog open={openSettingsDialog} build={props.build} onClose={() => setOpenSettingsDialog(false)} />
            ) : null}
            <ConfirmDialog
                title={t("modals:deleteBuild.title")}
                open={isDeleteBuildDialogOpen}
                onConfirm={handleDelete}
                onClose={() => setIsDeleteBuildDialogOpen(false)}
                dangerButton
                primaryActionText={t("common:button.delete")}
            >
                <DialogContentText>{t("modals:deleteBuild.description", { version: props.build.version })}</DialogContentText>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};
